// Достаём initData из Telegram WebApp SDK.

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe?: {
          user?: {
            id?: number;
            first_name?: string;
            last_name?: string;
            username?: string;
          };
        };
        ready?: () => void;
        expand?: () => void;
        colorScheme?: "light" | "dark";
        themeParams?: Record<string, string>;
        MainButton?: {
          text: string;
          isVisible: boolean;
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
          setText: (text: string) => void;
          enable: () => void;
          disable: () => void;
          showProgress: (leaveActive?: boolean) => void;
          hideProgress: () => void;
          setParams: (p: {
            text?: string;
            color?: string;
            text_color?: string;
            is_active?: boolean;
            is_visible?: boolean;
          }) => void;
        };
        BackButton?: {
          isVisible: boolean;
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
        };
        HapticFeedback?: {
          impactOccurred: (style: "light" | "medium" | "heavy") => void;
          notificationOccurred: (type: "error" | "success" | "warning") => void;
        };
        showAlert?: (message: string, cb?: () => void) => void;
        showConfirm?: (
          message: string,
          cb?: (confirmed: boolean) => void,
        ) => void;
        openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
        // Telegram Bot API 8.0+ — нативное скачивание файла без выхода
        // из mini-app. Telegram сам спросит подтверждение и положит
        // файл в Files (iOS) / Downloads (Android) / в выбранную папку
        // (Desktop). Бот должен быть настроен в BotFather для этого.
        downloadFile?: (
          params: { url: string; file_name: string },
          callback?: (status: string) => void,
        ) => void;
        platform?: string;
        version?: string;
        // Bot API 8.0+ — полноэкранный режим (как у BotFather).
        isExpanded?: boolean;
        isFullscreen?: boolean;
        requestFullscreen?: () => void;
        exitFullscreen?: () => void;
        onEvent?: (event: string, cb: (...args: unknown[]) => void) => void;
        offEvent?: (event: string, cb: (...args: unknown[]) => void) => void;
      };
    };
  }
}

// Поддерживается ли нативный полноэкранный режим (Bot API 8.0+).
export function supportsFullscreen(): boolean {
  return typeof window.Telegram?.WebApp?.requestFullscreen === "function";
}

export function enterFullscreen(): void {
  window.Telegram?.WebApp?.requestFullscreen?.();
}

export function exitFullscreen(): void {
  window.Telegram?.WebApp?.exitFullscreen?.();
}

export function isFullscreenNow(): boolean {
  return Boolean(window.Telegram?.WebApp?.isFullscreen);
}

export function getInitData(): string {
  // В Telegram реальный initData приходит через WebApp.initData.
  // Для разработки в браузере можно подсунуть через ?init_data=… или
  // localStorage.setItem('debug_init_data', '...').
  const tg = window.Telegram?.WebApp;
  if (tg && tg.initData) return tg.initData;

  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get("init_data");
  if (fromQuery) return fromQuery;

  const fromStorage = localStorage.getItem("debug_init_data");
  if (fromStorage) return fromStorage;

  return "";
}

export function getLocalAuthUserId(): string | null {
  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get("local_auth_tg_id");
  if (fromQuery) return fromQuery;

  const fromStorage = localStorage.getItem("debug_local_auth_tg_id");
  if (fromStorage) return fromStorage;

  return null;
}

function isLocalhostHost(): boolean {
  const h = window.location.hostname.toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "::1";
}

export function isLocalAuthEnabled(): boolean {
  return isLocalhostHost() && getLocalAuthUserId() !== null;
}

export function getApiAuthHeaders(): Record<string, string> {
  const localAuthUserId = getLocalAuthUserId();
  if (isLocalhostHost() && localAuthUserId) {
    return { "X-Local-Auth-User-Id": localAuthUserId };
  }
  return { "X-Telegram-Init-Data": getInitData() };
}

export function tgReady(): void {
  const tg = window.Telegram?.WebApp;
  if (tg) {
    tg.ready?.();
    // Полноэкранный режим (как BotFather) при поддержке Bot API 8.0+,
    // иначе — деградация до обычного expand. Само сообщение пользователю
    // о доступности fullscreen на старых клиентах показывает UI (App.tsx).
    if (typeof tg.requestFullscreen === "function") {
      try {
        tg.requestFullscreen();
      } catch {
        tg.expand?.();
      }
    } else {
      tg.expand?.();
    }
  }
}

export function haptic(style: "light" | "medium" | "heavy" = "light"): void {
  window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
}

export function notifyHaptic(
  type: "error" | "success" | "warning" = "success",
): void {
  window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
}

// Подтверждение действия: нативный диалог Telegram, с откатом на
// браузерный window.confirm вне Telegram.
export function confirmAsync(message: string): Promise<boolean> {
  const tg = window.Telegram?.WebApp;
  if (tg && typeof tg.showConfirm === "function") {
    return new Promise<boolean>((resolve) => {
      tg.showConfirm!(message, (ok) => resolve(Boolean(ok)));
    });
  }
  return Promise.resolve(window.confirm(message));
}

